import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../../AppIcon';
import Button from '../../ui/Button';
import Header from '../../ui/Header';
import Sidebar from '../../ui/Sidebar';
import SearchFilters from './components/SearchFilters';
import MatchingResults from './components/MatchingResults';
import SavedSearches from './components/SavedSearches';
import RecommendationEngine from './components/RecommendationEngine';

const ProgramDiscoveryEngine = () => {
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userRole, setUserRole] = useState('startup'); // startup, corporate, intermediary
  const [activeView, setActiveView] = useState('discover'); // discover, recommendations, saved
  const [searchFilters, setSearchFilters] = useState({});
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Mock data for programs and startups
  const mockPrograms = [
  {
    id: 'prog-1',
    title: "Global FinTech Accelerator 2024",
    company: "JPMorgan Chase",
    companyLogo: "https://images.unsplash.com/photo-1679552764936-705999cecf97",
    companyLogoAlt: "JPMorgan Chase modern office building with glass facade and corporate branding",
    description: "12-week intensive program for early-stage fintech startups focusing on payments, lending, and blockchain solutions. Access to mentors, customers, and $250K investment opportunity.",
    location: "New York, NY",
    duration: "12 weeks",
    funding: "$250K investment",
    participants: "15",
    status: "active",
    deadline: "2024-12-15",
    matchScore: 92,
    tags: ["FinTech", "Payments", "Blockchain", "B2B"],
    views: 1247,
    applications: 89,
    verified: true,
    createdAt: "2024-11-01",
    matchReasons: [
    "Matches your fintech focus area",
    "Suitable for your current funding stage",
    "Strong mentor network in payments"]

  },
  {
    id: 'prog-2',
    title: "Healthcare Innovation Lab",
    company: "Johnson & Johnson",
    companyLogo: "https://images.unsplash.com/photo-1624957866395-ef59c756e4d9",
    companyLogoAlt: "Johnson & Johnson healthcare research laboratory with modern medical equipment and scientists",
    description: "Partnership program for digital health startups developing solutions for patient care, medical devices, and healthcare analytics. 6-month collaboration with potential for long-term partnership.",
    location: "Boston, MA",
    duration: "6 months",
    funding: "Up to $500K",
    participants: "10",
    status: "active",
    deadline: "2024-11-30",
    matchScore: 87,
    tags: ["HealthTech", "Digital Health", "Medical Devices", "Analytics"],
    views: 892,
    applications: 56,
    verified: true,
    createdAt: "2024-10-28",
    matchReasons: [
    "Strong alignment with healthcare sector",
    "Matches your technology stack",
    "Previous success with similar startups"]

  },
  {
    id: 'prog-3',
    title: "AI Innovation Challenge",
    company: "Microsoft",
    companyLogo: "https://images.unsplash.com/photo-1684395703149-6ed0d196bca5",
    companyLogoAlt: "Microsoft modern office space with AI technology displays and collaborative workspaces",
    description: "Global competition for AI startups building enterprise solutions. Winners receive Azure credits, mentorship, and potential partnership opportunities with Microsoft's enterprise customers.",
    location: "Seattle, WA",
    duration: "4 months",
    funding: "$100K Azure credits",
    participants: "25",
    status: "active",
    deadline: "2024-12-01",
    matchScore: 95,
    tags: ["AI/ML", "Enterprise", "Cloud", "SaaS"],
    views: 2156,
    applications: 134,
    verified: true,
    createdAt: "2024-11-05",
    matchReasons: [
    "Perfect match for AI/ML expertise",
    "Enterprise focus aligns with your target market",
    "Strong technical mentorship available"]

  },
  {
    id: 'prog-4',
    title: "Sustainability Tech Accelerator",
    company: "Tesla",
    companyLogo: "https://images.unsplash.com/photo-1566939901787-16432e8e8b08",
    companyLogoAlt: "Tesla sustainable energy facility with solar panels and modern green architecture",
    description: "16-week program for startups developing clean energy, sustainable transportation, and environmental technology solutions. Focus on scalable impact and commercial viability.",
    location: "Austin, TX",
    duration: "16 weeks",
    funding: "$300K investment",
    participants: "12",
    status: "closing-soon",
    deadline: "2024-11-25",
    matchScore: 78,
    tags: ["CleanTech", "Sustainability", "Energy", "Transportation"],
    views: 743,
    applications: 67,
    verified: true,
    createdAt: "2024-10-15",
    matchReasons: [
    "Growing interest in sustainability sector",
    "Good fit for your innovation profile"]

  }];


  const mockStartups = [
  {
    id: 'startup-1',
    name: "PayFlow Solutions",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_163667fd7-1763103481630.png",
    logoAlt: "PayFlow Solutions modern fintech company logo with digital payment interface design",
    tagline: "Next-gen payment infrastructure for emerging markets",
    description: "Building scalable payment solutions for underbanked populations in Southeast Asia. Our platform processes over $50M in transactions monthly with 99.9% uptime.",
    location: "Singapore",
    founded: "2022",
    teamSize: "25",
    funding: "$2.5M raised",
    stage: "seed",
    matchScore: 94,
    technologies: ["Blockchain", "Mobile Payments", "API", "Security"],
    growth: "300",
    verified: true,
    featured: true,
    achievements: [
    "Processed $50M+ in transactions",
    "99.9% platform uptime",
    "Featured in TechCrunch"],

    matchReasons: [
    "Strong fintech focus matches your program",
    "Proven traction and growth metrics",
    "Experienced team with domain expertise"]

  },
  {
    id: 'startup-2',
    name: "MedAI Diagnostics",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1f1be1967-1763103483892.png",
    logoAlt: "MedAI Diagnostics healthcare technology company logo with medical AI visualization elements",
    tagline: "AI-powered medical imaging for early disease detection",
    description: "Developing machine learning algorithms for radiology that can detect cancer 40% earlier than traditional methods. FDA breakthrough device designation received.",
    location: "Boston, MA",
    founded: "2021",
    teamSize: "18",
    funding: "$4.2M raised",
    stage: "series-a",
    matchScore: 89,
    technologies: ["AI/ML", "Medical Imaging", "Computer Vision", "Healthcare"],
    growth: "250",
    verified: true,
    featured: false,
    achievements: [
    "FDA Breakthrough Device Designation",
    "40% improvement in early detection",
    "Partnership with 3 major hospitals"],

    matchReasons: [
    "Healthcare AI aligns with program focus",
    "Strong regulatory progress",
    "Proven clinical outcomes"]

  }];


  // Determine result type based on user role
  const getResultType = () => {
    return userRole === 'corporate' ? 'startups' : 'programs';
  };

  // Get appropriate mock data based on user role
  const getMockData = () => {
    return userRole === 'corporate' ? mockStartups : mockPrograms;
  };

  // Handle search with filters
  const handleSearch = (filters = searchFilters, query = searchQuery) => {
    setLoading(true);
    setSearchFilters(filters);
    setSearchQuery(query);

    // Simulate API call
    setTimeout(() => {
      let results = getMockData();

      // Apply filters
      if (filters?.industry) {
        results = results?.filter((item) =>
        item?.tags?.some((tag) => tag?.toLowerCase()?.includes(filters?.industry?.toLowerCase())) ||
        item?.technologies?.some((tech) => tech?.toLowerCase()?.includes(filters?.industry?.toLowerCase()))
        );
      }

      if (filters?.stage) {
        results = results?.filter((item) => item?.stage === filters?.stage);
      }

      if (filters?.location) {
        results = results?.filter((item) =>
        item?.location?.toLowerCase()?.includes(filters?.location?.toLowerCase())
        );
      }

      if (filters?.verified) {
        results = results?.filter((item) => item?.verified);
      }

      if (query) {
        results = results?.filter((item) =>
        item?.title?.toLowerCase()?.includes(query?.toLowerCase()) ||
        item?.name?.toLowerCase()?.includes(query?.toLowerCase()) ||
        item?.description?.toLowerCase()?.includes(query?.toLowerCase()) ||
        item?.company?.toLowerCase()?.includes(query?.toLowerCase())
        );
      }

      setSearchResults(results);
      setLoading(false);
    }, 1500);
  };

  // Initial search on component mount
  useEffect(() => {
    handleSearch();
  }, [userRole]);

  const handleFiltersChange = (newFilters) => {
    handleSearch(newFilters, searchQuery);
  };

  const handleViewDetails = (item) => {
    console.log('View details:', item);
    // Navigate to details page or open modal
  };

  const handleApply = (program) => {
    console.log('Apply to program:', program);
    // Navigate to application page or open modal
  };

  const handleContact = (startup) => {
    console.log('Contact startup:', startup);
    // Open contact modal or navigate to contact page
  };

  const handleViewProfile = (startup) => {
    console.log('View startup profile:', startup);
    // Navigate to startup profile page
  };

  const handleLoadSavedSearch = (search) => {
    setSearchFilters(search?.filters);
    setSearchQuery(search?.query || '');
    handleSearch(search?.filters, search?.query || '');
    setActiveView('discover');
  };

  const handleDeleteSavedSearch = (searchId) => {
    console.log('Delete saved search:', searchId);
    // API call to delete saved search
  };

  const views = [
  { id: 'discover', label: 'Discover', icon: 'Search' },
  { id: 'recommendations', label: 'Recommendations', icon: 'Sparkles' },
  { id: 'saved', label: 'Saved Searches', icon: 'Bookmark' }];


  const roleOptions = [
  { id: 'startup', label: 'Startup Founder', description: 'Looking for programs and opportunities' },
  { id: 'corporate', label: 'Corporate Innovator', description: 'Scouting for startups and partnerships' },
  { id: 'intermediary', label: 'Innovation Scout', description: 'Connecting startups with opportunities' }];


  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <main className={`transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'} pt-16`}>
        <div className="p-6 max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Program Discovery Engine</h1>
                <p className="text-muted-foreground">
                  AI-powered matching between corporate challenges and startup solutions
                </p>
              </div>
              
              {/* Role Selector */}
              <div className="flex items-center space-x-2 bg-card border border-border rounded-lg p-1">
                {roleOptions?.map((role) =>
                <button
                  key={role?.id}
                  onClick={() => setUserRole(role?.id)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  userRole === role?.id ?
                  'bg-primary text-primary-foreground shadow-sm' :
                  'text-muted-foreground hover:text-foreground hover:bg-muted'}`
                  }
                  title={role?.description}>

                    {role?.label}
                  </button>
                )}
              </div>
            </div>

            {/* View Navigation */}
            <div className="flex items-center space-x-1 bg-muted p-1 rounded-lg w-fit">
              {views?.map((view) =>
              <button
                key={view?.id}
                onClick={() => setActiveView(view?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeView === view?.id ?
                'bg-background text-foreground shadow-sm' :
                'text-muted-foreground hover:text-foreground'}`
                }>

                  <Icon name={view?.icon} size={16} />
                  <span>{view?.label}</span>
                </button>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className="xl:col-span-3 space-y-6">
              {activeView === 'discover' &&
              <>
                  <SearchFilters
                  onFiltersChange={handleFiltersChange}
                  activeFilters={searchFilters} />

                  <MatchingResults
                  results={searchResults}
                  resultType={getResultType()}
                  loading={loading}
                  userRole={userRole}
                  onViewDetails={handleViewDetails}
                  onApply={handleApply}
                  onContact={handleContact}
                  onViewProfile={handleViewProfile} />

                </>
              }

              {activeView === 'recommendations' &&
              <RecommendationEngine
                userRole={userRole}
                onViewDetails={handleViewDetails}
                onApply={handleApply}
                onContact={handleContact}
                onViewProfile={handleViewProfile} />

              }

              {activeView === 'saved' &&
              <SavedSearches
                onLoadSearch={handleLoadSavedSearch}
                onDeleteSearch={handleDeleteSavedSearch} />

              }
            </div>

            {/* Sidebar Content */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Discovery Stats</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Active Programs</span>
                    <span className="font-semibold text-foreground">1,247</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Verified Startups</span>
                    <span className="font-semibold text-foreground">3,892</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Successful Matches</span>
                    <span className="font-semibold text-foreground">456</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">This Month</span>
                    <span className="font-semibold text-success">+23%</span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Plus"
                    iconPosition="left">

                    {userRole === 'corporate' ? 'Create Program' : 'Submit Application'}
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Bell"
                    iconPosition="left">

                    Set Alerts
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Download"
                    iconPosition="left">

                    Export Results
                  </Button>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-success rounded-full"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground truncate">New match found</p>
                      <p className="text-xs text-muted-foreground">2 minutes ago</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground truncate">Application submitted</p>
                      <p className="text-xs text-muted-foreground">1 hour ago</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-warning rounded-full"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground truncate">Profile updated</p>
                      <p className="text-xs text-muted-foreground">3 hours ago</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>);

};

export default ProgramDiscoveryEngine;